import React, { useEffect, useState, useContext } from "react";
import DataTable from "react-data-table-component";
import InfoIcon from "@mui/icons-material/Info";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import NotificationModal from "../page/NotificationModal.jsx";
import CircleIcon from "@mui/icons-material/Circle";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { ImageContext } from "../page/ApiContext.jsx";
import { getCookie } from "../components/Cookie.jsx";
import FadeLoader from "react-spinners/FadeLoader";
import "../index.css";

const customStyles = {
  rows: {
    style: {
      minHeight: "3rem", // Override the row height
      fontSize: "0.8rem", // Set font size to small (14px)
      textAlign: "center",
    },
  },
  cells: {
    style: {
      fontSize: "0.8rem", // Set font size to small (14px)
      textAlign: "center", // Center-align the text in cells
      display: "flex",
      justifyContent: "center",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#f1f4f9", // Set header background color to light gray
      color: "black", // Set header text color to black
      fontSize: "0.7rem", // Set font size to small (14px)
      fontWeight: "bold", // Make font bold
      textAlign: "center", // Center-align the text in header cells
      textAlign: "center", // Center-align the text in cells
      display: "flex",
      justifyContent: "center",
    },
  },
};

const NotificationDashboard = () => {
  const [layout, setLayout] = useState(undefined);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [apiData, setApiData] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    open: 0,
    live: 0,
    overdue: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [requestType, setRequestType] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all"); // New state for dropdown filter
  const { rowId, setRowId } = useContext(ImageContext);
  const [loading, setLoading] = useState(false);
  const token = getCookie("sqa_token");
  const username = localStorage.getItem("email");

  const {
    dashboardIds,
    setDashboardIds,
    functionalityIds,
    setFunctionalityIds,
    userRoleId,
    setUserRoleId,
  } = useContext(ImageContext);

  const handleRequestTypeChange = (event) => {
    setRequestType(event.target.value);
  };

  const handleAssignedFilterChange = (event) => {
    setAssignedFilter(event.target.value);
  };

  useEffect(() => {
    dashboardData();
  }, []);

  useEffect(() => {
    if (rowId) {
      const index = apiData.findIndex((item) => item._id === rowId);
    }
  }, [rowId]);

  const dashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/sqa/api/notification_dashboard_data/?username=${username}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log("dataaaa", data);

      setApiData(data?.result[0] || []);

      setSearchQuery(rowId || "");

      const counts = data?.result[0].reduce(
        (acc, item) => {
          switch (item.status.toLowerCase()) {
            case "open":
              acc.open += 1;
              break;
            case "live":
              acc.live += 1;
              break;
            case "overdue":
              acc.overdue += 1;
              break;
            default:
              break;
          }
          return acc;
        },
        { open: 0, live: 0, overdue: 0 }
      );

      setStatusCounts(counts);
      setRowId(null);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
    setLoading(false);
  };

  // Filter data based on search query, request type, and assigned filter
  const filteredData = apiData.filter((item) => {
    const query = searchQuery.toLowerCase();

    // Helper function to safely check if a field matches the query
    const matchesQuery = (field) => {
      if (field == null) return false; // This checks for both null and undefined
      return String(field).toLowerCase().includes(query);
    };

    // Check if any field matches the search query
    const matchesSearchQuery =
      matchesQuery(item.assignedTo) ||
      matchesQuery(item.comment) ||
      matchesQuery(item.createdAt) ||
      matchesQuery(item.createdBy) ||
      matchesQuery(item.description) ||
      matchesQuery(item.dueDate) ||
      matchesQuery(item.file) ||
      matchesQuery(item.requestId) ||
      matchesQuery(item.batchId) ||
      matchesQuery(item.status) ||
      matchesQuery(item.title) ||
      (Array.isArray(item.type) && item.type.some(matchesQuery)) ||
      matchesQuery(item.updatedAt) ||
      matchesQuery(item._id);

    const matchesRequestType =
      requestType === "all" || item.type === requestType;
    const matchesAssignedFilter =
      assignedFilter === "all" ||
      (assignedFilter === "assignedToMe" && item.assignedTo === username) ||
      (assignedFilter === "assignedByMe" && item.createdBy === username);

    return matchesSearchQuery && matchesRequestType && matchesAssignedFilter;
  });

  const insertBreaks = (str, interval = 5) => {
    return str.replace(new RegExp(`(.{${interval}})`, "g"), "$1&#8203;");
  };
  const column = [
    {
      name: (
        <div className="whitespace-normal break-words text-left">
          Batch ID / Request ID
        </div>
      ),
      width: "16.5rem",

      selector: (row) => (row.type === "batch" ? row.batchId : row.requestId),
      sortable: true,
      cell: (row) => (
        <div
          className="whitespace-normal break-words text-left"
          dangerouslySetInnerHTML={{
            __html: insertBreaks(
              row.type === "batch" ? row.batchId : row.requestId
            ),
          }}
        />
      ),
    },
    {
      name: <div className="pl-4">Title</div>,
      width: "full",
      selector: (row) => row.title,
      sortable: true,
      cell: (row) => (
        <div className="whitespace-normal break-words text-center">
          {row.title}
        </div>
      ),
    },
    {
      name: <div className="text-center">Created On</div>,
      width: "12rem",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <div className="whitespace-normal break-words text-left">
          {row.createdAt}
        </div>
      ),
    },
    {
      name: <div className="text-center">Assigned By </div>,
      width: "full",
      selector: (row) => row.createdBy,
      sortable: true,
      cell: (row) => (
        <div className="whitespace-normal break-words text-left">
          {row.createdBy}
        </div>
      ),
    },
    {
      name: <div className="text-center">Assigned To</div>,
      width: "full",
      selector: (row) => row?.assignedTo,
      sortable: true,
      cell: (row) => {
        const length = row?.assignedTo?.length || 0;
        return (
          <div className="whitespace-normal break-words text-left">
            {length > 0
              ? `${row.assignedTo[0]}${length > 1 ? `+@${length - 1}` : ""}`
              : "No assignees"}
          </div>
        );
      },
    },
    {
      name: <div>Due Date</div>,
      width: "8rem",
      selector: (row) => row.dueDate || "-",
      sortable: true,
    },
    {
      name: <div>Message Status</div>,
      width: "8rem",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let textColorClass;

        // Check if status exists and is a string before formatting
        const formattedStatus =
          typeof row.status === "string"
            ? row.status.charAt(0).toUpperCase() +
              row.status.slice(1).toLowerCase()
            : " - "; // Default value if status is undefined or not a string

        switch (formattedStatus) {
          case "Open":
            textColorClass =
              "text-blue-500 bg-[#dae6ff] w-[5rem] py-2 rounded-md";
            break;
          case "Overdue":
            textColorClass =
              "text-red-500 bg-[#fbccd6] w-[5rem] py-2 rounded-md";
            break;
          case "Live":
            textColorClass =
              "text-purple-500 bg-[#e0d4fc] w-[5rem] py-2 rounded-md";
            break;
          default:
            textColorClass = "text-gray-700";
        }

        return (
          <div className={`whitespace-normal break-words ${textColorClass}`}>
            {formattedStatus}
          </div>
        );
      },
    },
    // {
    //   name: <div>Last Action</div>,
    //   width: "full",
    //   selector: (row) => " - ",
    //   sortable: true,
    // },
  ];

  if (functionalityIds.includes(205)) {
    column.push({
      name: <div className="">View</div>,
      width: "5rem",
      selector: (row) => row.status,
      sortable: false,
      cell: (row) => (
        <div
          onClick={() => {
            setModalData(row);
            setLayout("fullscreen");
            setSelectedRowId(row?._id);
          }}
          className="border border-black p-1 rounded-md cursor-pointer border-blue-500 text-blue-500 hover:text-white hover:bg-blue-500"
          style={{ whiteSpace: "normal", wordWrap: "break-word" }}
        >
          <PlagiarismIcon />
        </div>
      ),
    });
  }
  const selectStyle = {
    fontSize: "0.875rem", // Smaller font size
    "& .MuiSelect-select": {
      paddingTop: "0.5rem",
      paddingBottom: "0.5rem",
    },
  };

  return (
    <>
      {loading ? (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <FadeLoader
            color="#88adff"
            loading={loading}
            size={150}
            aria-label="Loading Spinner"
            data-testid="loader"
            speedMultiplier
          />
        </div>
      ) : (
        <div className="w-[100%] h-[100vh] pt-20">
          <div className="space-y-4">
            {" "}
            {/* Parent div with spacing between children */}
            <div className="p-4 justify-between flex items-center text-xs">
              {" "}
              {/* Third child */}
              <div className="flex space-x-4">
                {" "}
                {/* Flex container for cards */}
                <div className="w-20 h-20 border-2 rounded-lg border-[#00B69B] flex flex-col items-center justify-center text-[#00B69B] font-bold text-xs">
                  {" "}
                  {/* Green card */}
                  <span className="text-xs ">Open</span>
                  <span className="text-md">{statusCounts.open}</span>
                </div>
                <div className="w-20 h-20  border-2 rounded-lg border-[#EA0234] flex flex-col items-center justify-center text-[#EA0234] font-bold text-xs ">
                  {" "}
                  {/* Red card */}
                  <span className="text-xs">Overdue</span>
                  <span className="text-xs">{statusCounts.overdue}</span>
                </div>
                <div className="w-20 h-20  border-2 rounded-lg border-[#6226EF] flex flex-col items-center justify-center text-[#6226EF] font-bold text-xs">
                  {" "}
                  {/* Blue card */}
                  <span className="text-xs">Live</span>
                  <span className="text-xs">{statusCounts.live}</span>
                </div>
              </div>
              <div className="flex flex-row gap-10">
                <div className="flex gap-4 items-center">
                  <label
                    htmlFor="requestTypeDropdown"
                    className="text-gray-700 font-semibold"
                  >
                    Request Id / Batch Id:
                  </label>
                  <FormControl sx={{ minWidth: 100 }}>
                    <Select
                      id="requestTypeDropdown"
                      value={requestType}
                      onChange={handleRequestTypeChange}
                      sx={selectStyle}
                      size="small"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="batch">Batch Id</MenuItem>
                      <MenuItem value="request">Request Id</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="flex gap-4 items-center">
                  <label
                    htmlFor="assignedByDropdown"
                    className="text-gray-700 font-semibold"
                  >
                    Assigned By me / To me:
                  </label>
                  <FormControl sx={{ minWidth: 100 }}>
                    <Select
                      id="assignedByDropdown"
                      value={assignedFilter}
                      onChange={handleAssignedFilterChange}
                      sx={selectStyle}
                      size="small"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="assignedToMe">Assigned To Me</MenuItem>
                      <MenuItem value="assignedByMe">Assigned By me</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="flex gap-4 items-center">
                  {/* <div className="font-semibold text-3xl">Notification</div> */}
                  <div className="flex space-x-4">
                    <div className="text-xs">
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline rounded-full"
                        id="searchFilter"
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>{" "}
                    {/* Row for right side cards */}
                    <div
                      className="relative text-blue-400 flex items-center justify-center cursor-pointer "
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                    >
                      <InfoIcon />
                      {isHovered && (
                        <div className="absolute left-[-20rem] top-2 mb-2 w-70 p-3  bg-white border rounded shadow-lg text-sm text-gray-700 z-50">
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2 text-[#00B69B]">
                              <CircleIcon />
                              <p className="text-black">
                                Open : Ticket has been raised
                              </p>
                            </div>
                            <div className="flex gap-2 text-[#EA0234]">
                              <CircleIcon />
                              <p className="text-black">
                                Overdue : Ticket has been outdated
                              </p>
                            </div>
                            <div className="flex gap-2 text-[#6226EF]">
                              <CircleIcon />
                              <p className="text-black">
                                Live : Ticket has been resolved and live
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pl-4 rounded-md">
            <DataTable
              columns={column}
              data={filteredData} // Use filtered data for live search and dropdown
              // progressPending={apiData.length === 0}
              pagination
              customStyles={customStyles}
              noDataComponent={<div className="py-10">No Records Found</div>}
              persistTableHead
              defaultSortFieldId={3}
              // defaultSortAsc={false}
              fixedHeader="true"
              fixedHeaderScrollHeight="80vh" // Set the desired height for the fixed header
            />
          </div>
          <NotificationModal
            setLayout={setLayout}
            layout={layout}
            selectedRowId={selectedRowId}
            modalData={modalData} // Pass modalData to NotificationModal
            dashboardFn={dashboardData} // Pass function reference
          />
        </div>
      )}
    </>
  );
};

export default NotificationDashboard;
